import { Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "../lib/utils";

type Stage = { label: string; duration: number };

function getStages(opts: {
  allSourcesAreText: boolean;
  hasYoutubeSource: boolean;
}): Stage[] {
  const { allSourcesAreText, hasYoutubeSource } = opts;
  const intakeStages: Stage[] = [
    {
      label: hasYoutubeSource
        ? "Watching your video"
        : allSourcesAreText
          ? "Reading your notes"
          : "Reading your article",
      duration: 15_000,
    },
  ];

  return [
    ...intakeStages,
    { label: "Pulling the strongest angles", duration: 20_000 },
    { label: "Planning your posts", duration: 20_000 },
  ];
}

// After the scripted stages run out (~55s) the confirmation window can keep
// waiting up to 180s — switch to honest copy instead of leaving the last
// stage spinning under a broken "about a minute" promise.
const LONG_WAIT_MS = 65_000;

export function GenerateWorkingState({
  allSourcesAreText,
  hasYoutubeSource,
}: {
  allSourcesAreText: boolean;
  hasYoutubeSource: boolean;
}) {
  const stages = getStages({ allSourcesAreText, hasYoutubeSource });
  const [stageIndex, setStageIndex] = useState(0);
  const [isLongWait, setIsLongWait] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsLongWait(true), LONG_WAIT_MS);
    return () => clearTimeout(t);
  }, []);

  // Advance through stages on their scripted timers; stop at the last one so
  // copy never loops back (that would contradict itself).
  useEffect(() => {
    if (stageIndex >= stages.length - 1) return;
    const t = setTimeout(() => {
      setStageIndex((s) => Math.min(s + 1, stages.length - 1));
    }, stages[stageIndex].duration);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageIndex]);

  // Only render stages we've reached — future stages don't exist yet in the UI.
  const visibleStages = stages.slice(0, stageIndex + 1);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center px-6">
      {/* Dim, blurred backdrop — the form shows through faintly */}
      <div className="absolute inset-0 bg-background/85 backdrop-blur-md" />

      {/* Floating text — no panel, no border, just typography on the backdrop */}
      <div
        role="status"
        aria-live="polite"
        className="relative max-w-md w-full text-center"
      >
        <h3 className="text-lg font-semibold text-foreground">
          Making your posts…
        </h3>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {isLongWait
            ? "This one's taking longer than usual — we're still working on it. Hang tight."
            : "This usually takes about a minute."}
        </p>
        <ul className="mt-7 space-y-3 inline-block text-left">
          {visibleStages.map((stage, idx) => {
            const isDone = idx < stageIndex;
            return (
              <li
                key={idx}
                className={cn(
                  "flex items-center gap-3 text-sm",
                  "animate-in fade-in slide-in-from-bottom-1 duration-500",
                  isDone ? "text-muted-foreground" : "text-foreground font-medium"
                )}
              >
                {isDone ? (
                  <Check className="h-4 w-4 text-primary shrink-0" />
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                )}
                <span>{stage.label}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
